/**
 * ProtectedRoute Component
 *
 * Route protection components for wouter navigation: signed-in only
 * (`ProtectedRoute`) and signed-out only (`PublicOnlyRoute`).
 *
 * Uses AuthContext for authentication state.
 */

import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/core/contexts/AuthContext';
import { navigateAfterAuth } from '@/features/platform/auth/utils/post-auth-redirect';
import { LoadingSpinner } from '../shell/LoadingSpinner';

// ═══════════════════════════════════════════════════════════════════════
// LOADING FALLBACK
// ═══════════════════════════════════════════════════════════════════════

const LoadingFallback = () => <LoadingSpinner />;

const LOGIN_PATH = '/login';

// ═══════════════════════════════════════════════════════════════════════
// PROTECTED ROUTE
// ═══════════════════════════════════════════════════════════════════════

/**
 * ProtectedRoute — renders its children for a signed-in citizen and sends
 * anyone else to /login, remembering where they were going.
 *
 * The one route guard for "signed in". A route that also needs a journey wraps
 * its page in `<JourneyInstanceGuard>` inside this (see `JourneyRequiredRoute`
 * in core/routes/index.tsx), which shows an in-page prompt instead of a
 * redirect.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const hasRedirected = useRef(false);

  // Stabilize setLocation via ref so it never triggers effect re-runs
  const setLocationRef = useRef(setLocation);
  setLocationRef.current = setLocation;
  const navigate = useCallback((to: string) => setLocationRef.current(to), []);

  useEffect(() => {
    if (isLoading || isAuthenticated || hasRedirected.current) return;
    hasRedirected.current = true;
    // Store current path for redirect after login (skip if user is actively
    // logging out). Include the query string + hash — otherwise an auth
    // bounce drops params like `?sponsorshipToken=…`, so the sponsorship
    // offer dialog never opens on the cold-load path (it worked only on a
    // warm reload where no bounce happens). Root cause of the intermittent
    // "click once → nothing, click again → dialog" behaviour.
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      if (currentPath !== '/' && currentPath !== LOGIN_PATH) {
        sessionStorage.setItem('tonnie_redirect_after_auth', currentPath);
      }
    }
    navigate(LOGIN_PATH);
  }, [isLoading, isAuthenticated, navigate]);

  // Loading, or on the way to /login.
  if (isLoading || !isAuthenticated) {
    return <LoadingFallback />;
  }

  return <>{children}</>;
}

// ═══════════════════════════════════════════════════════════════════════
// PUBLIC ONLY ROUTE
// ═══════════════════════════════════════════════════════════════════════

/**
 * PublicOnlyRoute - For routes accessible only when NOT logged in
 */
export function PublicOnlyRoute({
  children,
  redirectTo = '/dashboard',
}: {
  children: ReactNode;
  redirectTo?: string;
}) {
  const { isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const hasRedirected = useRef(false);

  // Stabilize setLocation via ref so it never triggers effect re-runs
  const setLocationRef = useRef(setLocation);
  setLocationRef.current = setLocation;
  const navigate = useCallback((to: string) => setLocationRef.current(to), []);

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      // Destination priority: an explicit `?redirect_url=` (the sponsorship-
      // offer / accept-invite email links pass it so an already-authed visitor
      // lands back on the deep link WITH its query — without this the token was
      // dropped and the offer dialog never opened), then the stored post-auth
      // path, then the default. `navigateAfterAuth` validates the param
      // (same-origin path or allow-listed host) to prevent open-redirects.
      const storedRedirect = sessionStorage.getItem('tonnie_redirect_after_auth');
      sessionStorage.removeItem('tonnie_redirect_after_auth');
      navigateAfterAuth(storedRedirect || redirectTo, navigate);
    }
  }, [isLoading, isAuthenticated, redirectTo, navigate]);

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (isAuthenticated) {
    return <LoadingFallback />;
  }

  return <>{children}</>;
}
